import type { ReactNode } from 'react';
import {
  CalendarOff,
  CalendarRange,
  CheckCircle2,
  Clock,
  FileText,
  MapPin,
  Video,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/ui/EmptyState';
import { formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ParentPeriod } from '../../types/overview';
import { OverviewCard } from './OverviewCard';

/** Where the day being looked at sits relative to today. */
export type DayState = 'past' | 'today' | 'future';

/**
 * What one period says it is.
 */
function statusOf(period: ParentPeriod, dayState: DayState, minutesNow: number): string {
  if (dayState === 'past') return 'Completed';
  if (dayState === 'future') return 'Scheduled';

  if (minutesNow < toMinutes(period.startTime)) return 'Upcoming';
  if (minutesNow <= toMinutes(period.endTime)) return 'In Progress';

  return 'Completed';
}

/**
 * Minutes since midnight at the school — India's clock, not the device's zone.
 *
 * The day itself is the server's `today`, read in IST; reading the hour from
 * the device's zone would put a parent abroad in the wrong period of the right
 * day.
 */
function schoolMinutesNow(): number {
  const [hours, minutes] = new Date()
    .toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour12: false })
    .split(':')
    .map(Number);

  return hours * 60 + minutes;
}

/** "09:45" → minutes since midnight, for placing a period against the clock. */
function toMinutes(clockTime: string): number {
  const [hours, minutes] = clockTime.split(':').map(Number);

  return hours * 60 + minutes;
}

export interface TodayScheduleProps {
  periods: ParentPeriod[];
  /** The day being looked at, already written out — "Thursday, 3 September 2026". */
  dayLabel: string;
  dayState: DayState;
  /** Named so the empty state can say which child has no class, not "this child". */
  childName: string;
  /** The holiday's name or "Week off" when the institute is shut that day. */
  closedFor: string | null;
}

export function TodaySchedule({
  periods,
  dayLabel,
  dayState,
  childName,
  closedFor,
}: TodayScheduleProps) {
  const minutesNow = schoolMinutesNow();

  return (
    <OverviewCard
      title={dayState === 'today' ? `Today's Schedule (${dayLabel})` : `Schedule (${dayLabel})`}
      icon={<CalendarRange className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />}
      action={
        <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-bold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
          {periods.length} {periods.length === 1 ? 'Period' : 'Periods'}
        </span>
      }
      footer={
        <Link
          to="/parent/timetable"
          className="flex items-center justify-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 transition-colors hover:text-indigo-700 dark:hover:text-indigo-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <CalendarRange className="h-4 w-4" aria-hidden />
          View full timetable
        </Link>
      }
    >
      {periods.length === 0 ? (
        <EmptyState
          icon={<CalendarOff className="h-5 w-5" />}
          title={closedFor ? `School closed — ${closedFor}` : 'No classes on this day'}
          description={
            closedFor
              ? `There are no lessons on ${dayLabel}. Choose another date at the top of the page to see ${childName}'s classes.`
              : `${childName} has no periods timetabled for ${dayLabel}. Choose another date at the top of the page to see that day's classes.`
          }
          className="py-10"
        />
      ) : (
        periods.map((period) => {
          const status = statusOf(period, dayState, minutesNow);

          return (
            <article
              key={period.uuid}
              className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 p-3.5 sm:p-4 transition-all hover:border-slate-300 dark:hover:border-slate-700 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-sm font-black text-white shadow-xs"
                  aria-hidden
                >
                  P{period.periodNumber}
                </span>

                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-slate-900 dark:text-white">
                    {period.subject}{' '}
                    <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
                      ({period.className})
                    </span>
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400" aria-hidden />
                      {formatTimeRange(period.startTime, period.endTime)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden />
                      {period.roomName ?? 'Room not set'}
                    </span>
                    <span className="truncate">{period.teacher ?? 'Teacher not assigned yet'}</span>
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2.5 self-start sm:self-auto">
                <div className="flex items-center gap-1.5 border-r border-slate-200 dark:border-slate-700/80 pr-2.5">
                  <PeriodLink
                    to="/parent/attendance"
                    label="Attendance register"
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    colorClass="border-indigo-500/30 text-indigo-500 hover:bg-indigo-500/10"
                  />
                  <PeriodLink
                    to="/parent/homework"
                    label={`Homework set in ${period.subject}`}
                    icon={<FileText className="h-4 w-4" />}
                    colorClass="border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10"
                  />
                  <PeriodLink
                    to="/parent/recorded-classes"
                    label={`Recorded classes for ${period.subject}`}
                    icon={<Video className="h-4 w-4" />}
                    colorClass="border-blue-500/30 text-blue-500 hover:bg-blue-500/10"
                  />
                </div>

                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-semibold border',
                    status === 'In Progress' &&
                      'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
                    status === 'Upcoming' &&
                      'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
                    (status === 'Completed' || status === 'Scheduled') &&
                      'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/20',
                  )}
                >
                  {status}
                </span>
              </div>
            </article>
          );
        })
      )}
    </OverviewCard>
  );
}

/** One of the round action buttons on a period row. */
function PeriodLink({
  to,
  label,
  icon,
  colorClass,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  colorClass?: string;
}) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        colorClass ??
          'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400',
      )}
    >
      {icon}
    </Link>
  );
}
